import useSWR from 'swr';
import { useMemo, useState } from 'react';
import useSWRMutation from 'swr/mutation';
import {
  fetcher,
  postFetcher,
  fetchGetRequest,
  handlePostRequestWithArg,
  handleDeleteRequestWithArg,
  handleGetRequestWithArg,
  handlePutRequestWithArg,
  tairraAxiosInstance,
} from 'src/utils/axios-tairra';
import { CLIENT_ID_KEY, EMPLOYEE_ID_KEY } from 'src/utils/constants';

import { apiEndpoints } from 'src/utils/api-endpoints/index';
import { mutate } from 'swr';
import { toast } from 'src/components/snackbar';
const swrOptions = {
  revalidateIfStale: true,
  revalidateOnFocus: true,
  revalidateOnReconnect: true,
  revalidateOnMount: true,
  dedupingInterval: 5000,
};

type Filter = {
  name?: string;
  operator?: string;
  value?: string | number | boolean;
  logic?: string;
  filters?: Filter[];
};

export function useTeamMemberListDisabled() {
  const [teamMemberListDisabled, setTeamMemberListDisabled] = useState([]);
  const [totalDisabled, setTotalDisabled] = useState(0);

  const { trigger, isMutating, error } = useSWRMutation(
    apiEndpoints.employee.filter,
    handlePostRequestWithArg,
    {
      onSuccess: (responseData) => {
        setTeamMemberListDisabled(responseData.data);
        setTotalDisabled(responseData.total);
      },
      onError: (error) => {
        const errorMessage = error?.returnMessage?.[0] || 'Error fetching data';
      },
    }
  );

  const getTeamMemberListDisabled = async (
    page: number,
    pageSize: number,
    searchTerm = '',
    isActive: boolean,
    category: number,
    sortField = 'FirstName',
    sortDirection = 'asc',
    clientId: number,
    deptId?: number
  ) => {
    const filters: Filter[] = [
      {
        name: 'EmployeeType',
        operator: 'eq',
        value: '1',
        logic: 'AND',
      },
      {
        name: 'IsActive',
        operator: 'eq',
        value: isActive,
        logic: 'AND',
      },
    ];

    if (deptId) {
      filters.push({
        name: 'DepartmentId',
        operator: 'eq',
        value: deptId,
        logic: 'AND',
      });
    }

    filters.push({
      logic: 'AND',
      filters: [
        {
          name: 'EmployeeStaffId',
          operator: 'contains',
          value: searchTerm,
          logic: 'OR',
        },
        {
          name: 'FirstName',
          operator: 'contains',
          value: searchTerm,
          logic: 'OR',
        },
        {
          name: 'Department.Name',
          operator: 'contains',
          value: searchTerm,
          logic: 'OR',
        },
        {
          name: 'Designation.Name',
          operator: 'contains',
          value: searchTerm,
          logic: 'OR',
        },
        {
          name: 'Email',
          operator: 'contains',
          value: searchTerm,
        },
      ],
    });

    const filterObject = {
      page,
      pageSize,
      category,
      sort: [
        {
          name: sortField,
          direction: sortDirection,
        },
      ],
      filter: {
        name: 'clientId',
        operator: 'eq',
        value: clientId,
        filters,
      },
    };

    await trigger(filterObject as any);
  };

  const memoizedValue = useMemo(() => {
    return {
      teamMemberListDisabled,
      totalDisabled,
      isLoadingDisabled: isMutating,
      errorDisabled: error,
      isEmpty: !teamMemberListDisabled.length,
      mutate: trigger,
    };
  }, [teamMemberListDisabled, totalDisabled, isMutating, error]);

  return {
    ...memoizedValue,
    getTeamMemberListDisabled,
  };
}

export function useTeamMemberList() {
  const [teamMemberList, setTeamMemberList] = useState([]);
  const [total, setTotal] = useState(0);

  const { trigger, isMutating, error } = useSWRMutation(
    apiEndpoints.employee.filterAssigned,
    handlePostRequestWithArg,
    {
      onSuccess: (responseData) => {
        setTeamMemberList(responseData.data);
        setTotal(responseData.total);
      },
      onError: (error) => {
        const errorMessage = error?.returnMessage?.[0] || 'Error fetching data';
      },
    }
  );

  const getTeamMemberList = async (
    page: number,
    pageSize: number,
    searchTerm = '',
    isActive: boolean,
    category: number,
    sortField = 'FirstName',
    sortDirection = 'asc',
    clientId: number,
    deptId?: number
  ) => {
    const filters: Filter[] = [
      {
        name: 'EmployeeType',
        operator: 'eq',
        value: '1',
        logic: 'AND',
      },
      {
        name: 'IsActive',
        operator: 'eq',
        value: isActive,
        logic: 'AND',
      },
    ];

    if (deptId) {
      filters.push({
        name: 'DepartmentId',
        operator: 'eq',
        value: deptId,
        logic: 'AND',
      });
    }

    filters.push({
      logic: 'AND',
      filters: [
        {
          name: 'EmployeeStaffId',
          operator: 'contains',
          value: searchTerm,
          logic: 'OR',
        },
        {
          name: 'FirstName',
          operator: 'contains',
          value: searchTerm,
          logic: 'OR',
        },
        {
          name: 'Department.Name',
          operator: 'contains',
          value: searchTerm,
          logic: 'OR',
        },
        {
          name: 'Designation.Name',
          operator: 'contains',
          value: searchTerm,
          logic: 'OR',
        },
        {
          name: 'Email',
          operator: 'contains',
          value: searchTerm,
        },
      ],
    });

    const filterObject = {
      page,
      pageSize,
      category,
      sort: [
        {
          name: sortField,
          direction: sortDirection,
        },
      ],
      filter: {
        name: 'clientId',
        operator: 'eq',
        value: clientId,
        filters,
      },
    };

    await trigger(filterObject as any);
  };

  const memoizedValue = useMemo(() => {
    return {
      teamMemberList,
      total,
      isLoading: isMutating,
      error,
      isEmpty: !teamMemberList.length,
      mutate: trigger,
    };
  }, [teamMemberList, total, isMutating, error]);

  return {
    ...memoizedValue,
    getTeamMemberList,
  };
}

export function useTeamMemberProfile(id: number) {
  const { data, error, isValidating } = useSWR(
    id !== undefined ? `${apiEndpoints.teams.getTeamMemberById}/${id}` : null,
    fetcher,
    swrOptions
  );

  const memoizedTeamMember = useMemo(() => {
    const teamMemberData = data?.data ?? [];

    const hasError = Boolean(error);

    return {
      teamMemberData,

      teamMemberLoading: !data && !error,
      teamMemberError: hasError ? error : null,
      teamMemberValidating: isValidating,
      teamMemberNotFound: !data?.data?.length && !error,
      mutateTeamMember: () => mutate([apiEndpoints.teams.getTeamMemberById]),
    };
  }, [data?.data, data?.total, error, isValidating, id]);

  return memoizedTeamMember;
}
export function useEmployeeChartData(id: number) {
  const { data, error, isValidating } = useSWR(
    id !== undefined ? `${apiEndpoints.projects.getEmployeeChartForTeam}/${id}` : null,
    fetcher,
    swrOptions
  );

  const memoizedEmployeeChartData = useMemo(() => {
    const employeeChartData = data?.data ?? [];

    const hasError = Boolean(error);

    return {
      employeeChartData,

      employeeChartDataLoading: !data && !error,
      employeeChartDataError: hasError ? error : null,
      employeeChartDataValidating: isValidating,
      employeeChartDataNotFound: !data?.data?.length && !error,
      mutateEmployeeChartData: () => mutate([apiEndpoints.projects.getEmployeeChartForTeam]),
    };
  }, [data?.data, data?.total, error, isValidating, id]);

  return memoizedEmployeeChartData;
}
export function useEmployeeDetails() {
  const { data, error, mutate, isValidating } = useSWR(
    (id: number) => (id ? `${apiEndpoints.teams.getEmployeeDetailsById}/${id}` : null),
    handleGetRequestWithArg,
    swrOptions
  );

  const employeeDetails = async (id: number) => {
    if (id === undefined || id === null) {
      console.error('Invalid ID:', id);
      toast.error('Invalid ID provided');
      return [];
    }
    try {
      const response = await handleGetRequestWithArg(
        `${apiEndpoints.teams.getEmployeeDetailsById}/${id}`,
        {}
      );
      const result = await response;
      mutate();
      return result;
    } catch (err) {
      console.error('Error fetching Team Member Details:', err);
      return [];
    }
  };

  return {
    employeeDetails,
    isLoading: isValidating,
    error,
  };
}

export function useEmployeeById(id: number) {
  const { data, error, isValidating } = useSWR(
    id !== undefined ? `${apiEndpoints.teams.getEmployeeDetailsById}/${id}` : null,
    fetcher
  );

  const memoizedTeamMember = useMemo(() => {
    const teamMemberData = data?.data ?? [];

    const hasError = Boolean(error);

    return {
      teamMemberData,

      teamMemberLoading: !data && !error,
      teamMemberError: hasError ? error : null,
      teamMemberValidating: isValidating,
      teamMemberNotFound: !data?.data?.length && !error,
      mutateTeamMember: () => mutate([apiEndpoints.teams.getEmployeeDetailsById]),
    };
  }, [data?.data, data?.total, error, isValidating]);

  return memoizedTeamMember;
}

export function useGetUserRightsById(UserId?: number) {
  const { data, error, mutate, isValidating } = useSWR(
    UserId ? `${apiEndpoints.teams.getEmployeeUserRightsById}?UserId=${UserId}` : null,
    handleGetRequestWithArg,
    swrOptions
  );

  const userRightsById = async (UserId: number) => {
    if (UserId === undefined || UserId === null) {
      console.error('Invalid ID:', UserId);
      toast.error('Invalid ID provided');
      return [];
    }
    try {
      const response = await handleGetRequestWithArg(
        `${apiEndpoints.teams.getEmployeeUserRightsById}?UserId=${UserId}`,
        {}
      );
      const result = await response;
      // console.log('user rights data:', result);
      mutate();
      return result;
    } catch (err) {
      console.error('Error fetching User Rights:', err);
      return [];
    }
  };

  return {
    userRightsById,
    isLoading: isValidating,
    error,
    mutate
  };
}
export function useGetDepartmentList(page: number, pageSize: number) {
  const requestData = {
    page,
    pageSize,
  };

  const { data, isLoading, error, isValidating, mutate } = useSWR(
    [apiEndpoints.teams.getDepartmentList, requestData],
    postFetcher,
    swrOptions
  );

  const memoizedValue = useMemo(() => {
    const departmentList = data?.data ?? [];
    const total = data?.total ?? 0;

    return {
      departments: { departmentList, total },
      deptLoading: isLoading,
      deptError: error,
      deptValidating: isValidating,
      deptEmpty: !isLoading && !departmentList.length,
      mutate,
    };
  }, [data, error, isValidating, page, pageSize]);

  return memoizedValue;
}
export function useGetDesignationList() {
  const { data, isLoading, error, isValidating, mutate } = useSWR(
    [apiEndpoints.teams.getDesignationList],
    fetchGetRequest,
    swrOptions
  );

  const memoizedValue = useMemo(() => {
    const designationList = data?.data ?? [];

    return {
      designations: { designationList },
      designationLoading: isLoading,
      designationError: error,
      designationValidating: isValidating,
      designationEmpty: !isLoading && !designationList.length,
      mutate,
    };
  }, [data, error, isValidating]);

  return memoizedValue;
}
export function useGetCountryList() {
  const { data, isLoading, error, isValidating, mutate } = useSWR(
    [apiEndpoints.teams.getCountryList],
    fetchGetRequest,
    swrOptions
  );

  const memoizedValue = useMemo(() => {
    const countryList = data?.data ?? [];

    return {
      countries: { countryList },
      countriesLoading: isLoading,
      countriesError: error,
      countriesValidating: isValidating,
      countriesEmpty: !isLoading && !countryList.length,
      mutate,
    };
  }, [data, error, isValidating]);

  return memoizedValue;
}
export function useSkillsList(page: number, pageSize: number) {
  const requestData = {
    page,
    pageSize,
  };

  const { data, isLoading, error, isValidating, mutate } = useSWR(
    [apiEndpoints.teams.getSkillList, requestData],
    postFetcher,
    swrOptions
  );

  const memoizedValue = useMemo(() => {
    const skillsList = data?.data ?? [];
    const total = data?.total ?? 0;

    return {
      skills: { skillsList, total },
      skillsLoading: isLoading,
      skillsError: error,
      skillsValidating: isValidating,
      skillsEmpty: !isLoading && !skillsList.length,
      mutateSkills: () => mutate([apiEndpoints.teams.getSkillList, requestData]),
    };
  }, [data, error, isValidating, page, pageSize]);

  return memoizedValue;
}
export function useGetUserRights() {
  const { data, isLoading, error, isValidating, mutate } = useSWR(
    [apiEndpoints.teams.getUserRightList],
    fetchGetRequest,
    swrOptions
  );

  const memoizedValue = useMemo(() => {
    const userRightList = data?.data ?? [];

    return {
      userRights: { userRightList },
      userRightsLoading: isLoading,
      userRightsError: error,
      userRightsValidating: isValidating,
      userRightsEmpty: !isLoading && !userRightList.length,
      mutate,
    };
  }, [data, error, isValidating]);

  return memoizedValue;
}
export function useAddEmployee() {
  const { trigger, isMutating, error } = useSWRMutation(
    apiEndpoints.teams.employeeInsert,
    handlePostRequestWithArg,
    {
      onSuccess: (responseData) => {
        if (responseData.isActive) {
          toast.success('Employee Added successfully');
        }
      },
      onError: (error) => {
        const errorMessage = error?.returnMessage?.[0] || 'Error creating user';
        toast.error(errorMessage);
      },
    }
  );

  const addEmployee = async (requestData: RequestData) => {
    // console.log('Request Data:', JSON.stringify(requestData));

    await trigger(requestData as any);
  };

  return {
    addEmployee,
    isLoading: isMutating,
    error,
  };
}
export function useUpdateEmployee() {
  const { data, error, mutate } = useSWR(
    (id: number) => (id ? `${apiEndpoints.teams.employeeUpdate}?id=${id}` : null),
    handlePutRequestWithArg,
    swrOptions
  );

  const updateEmployee = async (id: number, requestData: RequestData) => {
    if (id === undefined || id === null) {
      console.error('Invalid ID:', id);
      toast.error('Invalid ID provided');
      return [];
    }

    // console.log('Request Data:', JSON.stringify(requestData)); // Debugging log

    try {
      const response = await handlePutRequestWithArg(
        `${apiEndpoints.teams.employeeUpdate}?id=${id}`,
        requestData
      );
      const result = await response;
      // console.log('Updated employee data:', result);
      toast.success(result.returnMessage[0]);
      mutate();
      return result;
    } catch (err: any) {
      console.error('Error updating employee:', err);
      toast.error(err.returnMessage[0], { position: 'top-center' });
      return [];
    }
  };

  return {
    updateEmployee,
    isLoading: !data && !error,
    error,
  };
}
export function useEmployeeHierarchyList() {
  const { trigger, isMutating, error } = useSWRMutation(
    apiEndpoints.teams.employeeHeirarchy,
    handlePostRequestWithArg,
    {
      onSuccess: (responseData) => {
        console.log('Employee hierarchy fetched successfully', responseData);
      },
      onError: (error) => {
        const errorMessage = error?.returnMessage?.[0] || 'Error fetching employee hierarchy';
        toast.error(errorMessage);
      },
    }
  );

  const fetchEmployeeHierarchyList = async (page: number, pageSize: number) => {
    const requestData = {
      page,
      pageSize,
    };

    // console.log('Request Data:', JSON.stringify(requestData));

    try {
      const response = await trigger(requestData as any);
      return response;
    } catch (err) {
      console.error('Error triggering request:', err);
      return undefined;
    }
  };

  return {
    fetchEmployeeHierarchyList,
    isLoading: isMutating,
    error,
    mutate: trigger,
  };
}
export function useUnAssignedEmployee() {
  const { trigger, isMutating, error } = useSWRMutation(
    apiEndpoints.teams.getUnAssignedEmployee,
    handlePostRequestWithArg,
    {
      onSuccess: (responseData) => {
        console.log('Unasigned Employees fetched successfully');
      },
      onError: (error) => {
        const errorMessage = error?.returnMessage?.[0] || 'Error fetching unasigned employees';
        toast.error(errorMessage);
      },
    }
  );

  const fetchUnaasignedEmployeeList = async (page: number, pageSize: number, searchTerm = '') => {
    const requestData = {
      page,
      pageSize,
      filter: {
        filters: [
          { name: 'EmployeeType', operator: 'eq', value: 1, logic: 'AND' },
          { name: 'EmployeeStaffId', operator: 'contains', value: searchTerm, logic: 'OR' },
          { name: 'Department.Name', operator: 'contains', value: searchTerm, logic: 'OR' },
          { name: 'Designation.Name', operator: 'contains', value: searchTerm, logic: 'OR' },
          { name: 'FirstName', operator: 'contains', value: searchTerm },
        ],
      },
    };

    // console.log('Request Data:', JSON.stringify(requestData));

    try {
      const response = await trigger(requestData as any);
      return response;
    } catch (err) {
      console.error('Error triggering request:', err);
      return undefined;
    }
  };

  return {
    fetchUnaasignedEmployeeList,
    isLoading: isMutating,
    error,
  };
}
export function useAddEmployeeHeirarchy() {
  const { trigger, isMutating, error } = useSWRMutation(
    apiEndpoints.teams.employeeHierarchyInsert,
    handlePostRequestWithArg,
    {
      onSuccess: (responseData) => {
        if (responseData.isActive) {
          toast.success('Employee Added successfully');
        }
      },
      onError: (error) => {
        const errorMessage = error?.returnMessage?.[0] || 'Error creating user';
        toast.error(errorMessage);
      },
    }
  );

  const addHeirarchyEmployee = async (requestData: any) => {
    // console.log('Request Data:', JSON.stringify(requestData));
    try {
      await trigger(requestData);
    } catch (err) {
      console.error('Error adding employee to hierarchy:', err);
      throw err;
    }
  };

  return {
    addHeirarchyEmployee,
    isLoading: isMutating,
    error,
    mutate: trigger,
  };
}
export function useDeleteHeirarchyEmployee() {
  const { data, error, mutate } = useSWR(
    (id: number) => (id ? `${apiEndpoints.teams.employeeHierarchyDelete}?id=${id}` : null),
    handleDeleteRequestWithArg,
    swrOptions
  );

  const deleteHeirarchyEmployee = async (id: number) => {
    // console.log('Deleting employee with ID:', id);
    if (id === undefined || id === null) {
      console.error('Invalid ID:', id);
      toast.error('Invalid ID provided');
      return;
    }
    try {
      const response = await handleDeleteRequestWithArg(
        `${apiEndpoints.teams.employeeHierarchyDelete}?id=${id}`,
        {}
      );

      toast.success('Employee deactivated successfully');
      mutate();
    } catch (err: any) {
      console.error('Error deactivating employee:', err);
    }
  };

  return {
    deleteHeirarchyEmployee,
    isLoading: !data && !error,
    error,
  };
}
export function useAddEmployeeFile() {
  const [fileData, setFileData] = useState({});

  const { trigger, isMutating, error } = useSWRMutation(
    apiEndpoints.teams.employeeFileAdd,
    handlePostRequestWithArg,
    {
      onSuccess: (responseData) => {
        if (responseData && responseData.data) {
          setFileData({
            fileId: responseData.data.fileId,
            fullPath: responseData.data.fullPath,
          });
        }
      },
      onError: (error) => {
        // const errorMessage = error?.returnMessage?.[0] || 'Error creating user';
        // toast.error(errorMessage);
      },
    }
  );

  const uploadedFile = async (requestData: any) => {
    // console.log('Request Data:', JSON.stringify(requestData));
    try {
      const response = await trigger(requestData);
      return response;
    } catch (err) {
      console.error('Error adding employee file:', err);
      throw err;
    }
  };

  const memoizedValue = useMemo(() => {
    return {
      fileData,
      isLoading: isMutating,
      error,
      mutate: trigger,
    };
  }, [fileData, isMutating, error]);

  return {
    ...memoizedValue,
    uploadedFile,
  };
}
export function useGetTimesheet(page: number, pageSize: number, employeeId: number) {
  const requestData = {
    sort: [{ name: 'id', direction: 'desc' }],
    filter: {
      filters: [
        { name: 'employeeId', operator: 'eq', value: employeeId, logic: 'and' },
        { name: 'startDate', operator: 'lte', value: '2024-11-07' },
      ],
    },
    page: page,
    pageSize: pageSize,
  };

  const { data, isLoading, error, isValidating } = useSWR(
    [apiEndpoints.teams.getTimesheet, requestData],
    postFetcher,
    swrOptions
  );

  const memoizedValue = useMemo(() => {
    const timesheet = data?.data ?? [];
    const totalTimesheet = data?.total;

    return {
      data: { timesheet, totalTimesheet },
      timesheetLoading: isLoading,
      timesheetError: error,
      timesheetValidating: isValidating,
      timesheetEmpty: !isLoading && !timesheet.length,
      mutateTimesheet: () => mutate([apiEndpoints.teams.getTimesheet, requestData]),
    };
  }, [data?.data, data?.total, error, isLoading, isValidating, page, pageSize, employeeId]);

  return memoizedValue;
}
export function useGetAssignedTaskByStatus(
  status: number,
  pageNumber: number,
  pageSize: number,
  searchText: string | null,
  requestorId: number,
  employeeId: number
) {
  const requestData = {
    pageNumber,
    pageSize,
    priorityFlag: false,
    employeeId,
    filterPatern: 2,
    isOwner: false,
    requestorId: requestorId,
    searchText: searchText,
    status,
  };

  const { data, isLoading, error, isValidating } = useSWR(
    [apiEndpoints.tasks.getAllBySP, requestData],
    postFetcher,
    swrOptions
  );

  const memoizedValue = useMemo(() => {
    const tasks = data?.data ?? [];
    const total = data?.total ?? 0;

    return {
      assignedTasks: { tasks, total },
      assignedTasksLoading: isLoading,
      assignedTasksError: error,
      assignedTasksValidating: isValidating,
      assignedTasksEmpty: !isLoading && !tasks.length,
    };
  }, [
    data?.data,
    data?.total,
    error,
    isLoading,
    isValidating,
    status,
    pageNumber,
    pageSize,
    searchText,
    requestorId,
    employeeId,
  ]);

  return memoizedValue;
}
export function useReplaceEmployeeHeirarchy() {
  const { trigger, isMutating, error } = useSWRMutation(
    apiEndpoints.teams.employeeHierarchyReplace,
    handlePostRequestWithArg,
    {
      onSuccess: (responseData) => {
        if (responseData.isActive) {
          toast.success('Employee Added successfully');
        }
      },
      onError: (error) => {
        const errorMessage = error?.returnMessage?.[0] || 'Error creating user';
        toast.error(errorMessage);
      },
    }
  );

  const replaceHeirarchyEmployee = async (requestData: any) => {
    // console.log('Request Data:', JSON.stringify(requestData));
    try {
      await trigger(requestData);
    } catch (err) {
      console.error('Error adding employee to hierarchy:', err);
      throw err;
    }
  };

  return {
    replaceHeirarchyEmployee,
    isLoading: isMutating,
    error,
    mutate: trigger,
  };
}

export function useGetDepartments() {
  const GET_DEPARTMENTS = `${apiEndpoints.teams.getDepartments}`;

  const { data, error, isLoading, isValidating } = useSWR(GET_DEPARTMENTS, fetcher);

  const memoizedValue = useMemo(() => {
    const departmentList = data?.data ?? null;
    const hasError = Boolean(error);

    return {
      departmentList,
      departmentListLoading: isLoading,
      departmentListError: hasError ? error : null,
      departmentListValidating: isValidating,
      departmentListNotFound: !isLoading && !departmentList,
    };
  }, [data?.data, error, isLoading, isValidating]);

  return memoizedValue;
}

export function useGetEmployeeTarget(EmployeeId: number, Year: number) {
  const { data, error, isValidating } = useSWR(
    EmployeeId && Year // Only call the API if both EmployeeId and Year are valid
      ? `${apiEndpoints.teams.getEmployeeTarget}?EmployeeId=${EmployeeId}&Year=${Year}`
      : null,
    fetcher
  );

  const memoizedEmployeeTarget = useMemo(() => {
    const targetData = data?.data ?? [];
    const hasError = Boolean(error);

    return {
      targetData,
      targetDataLoading: !data && !error,
      targetDataError: hasError ? error : null,
      targetDataValidating: isValidating,
      targetDataNotFound: !data?.data?.length && !error,
      mutateTargetData: () => {
        const completeUrl = `${apiEndpoints.teams.getEmployeeTarget}?EmployeeId=${EmployeeId}&Year=${Year}`;
        mutate(completeUrl);
      },
    };
  }, [data?.data, error, isValidating, EmployeeId, Year]);

  return memoizedEmployeeTarget;
}

export async function addEmployeeTarget(data: any) {
  try {
    const response = await tairraAxiosInstance.post(apiEndpoints.teams.addEmployeeTarget, data);

    // console.log('This is the response', response);
    if (response?.data?.returnStatus) {
      toast.success('Employee Target Updated Successfully');

      // toast.success(response?.data?.returnMessage[0]);
      return { success: true, response };
    } else {
      return { success: false };
    }
  } catch (error: any) {
    const errorMessage = error?.response?.data?.returnMessage || ['Something went wrong!'];
    toast.error(errorMessage[0]);
    return { success: false };
  }
}

export async function changeEmployeePassword(data: any, userId: number) {
  try {
    const requestData = {
      userId: userId,
      password: data.updatedPassword,
    };

    const response = await tairraAxiosInstance.post(
      apiEndpoints.teams.resetEmployeePassword,
      requestData
    );

    if (response?.data?.returnStatus === true) {
      // toast.success(response.data.returnMessage[0])
      toast.success('Password Updated Successfully');

      return {
        success: true,
      };
    } else {
      toast.error(response.data.returnMessage[0]);
    }
  } catch (error: any) {
    const errorMessage = error.response?.data?.errorMessage || 'Something went wrong!';
    const errorCodes = error.response?.data?.errorsCodes || [];
    const statusCode = error.response?.data?.statusCode || null;

    return {
      success: false,
      error: {
        message: errorMessage,
        codes: errorCodes,
        statusCode: statusCode,
      },
    };
  }
}

export function useGetTaskCompletionByEmployee(
  projectId?: number,
  milestoneId?: number,
  startDate?: string,
  endDate?: number,
  employeeId?: number
) {
  const requestData = {
    projectId: projectId,
    milestoneId: milestoneId,
    startDate: startDate,
    endDate: endDate,
    employeeId: employeeId,
  };
  const { data, isLoading, error, isValidating } = useSWR(
    [apiEndpoints.teams.getTaskCompletionByEmployee, requestData],
    postFetcher,
    swrOptions
  );

  const memoizedValue = useMemo(() => {
    const taskData = data?.data ?? {};

    return {
      employeeTasks: { taskData },
      employeeTasksLoading: !data && !error,
      employeeTasksError: error,
      employeeTasksValidating: isValidating,

      mutateEmployeeTasks: () =>
        mutate([apiEndpoints.teams.getTaskCompletionByEmployee, requestData]),
    };
  }, [data, error, isValidating, projectId, milestoneId]);

  return memoizedValue;
}

export function useGetTaskCompletionByDepartment(
  projectId?: number,
  milestoneId?: number,
  startDate?: string,
  endDate?: number,
  departmentIdList?: []
) {
  const requestData = {
    projectId: projectId,
    milestoneId: milestoneId,
    startDate: startDate,
    endDate: endDate,
    departmentIdList: departmentIdList ? departmentIdList : [],
  };
  const { data, isLoading, error, isValidating } = useSWR(
    [apiEndpoints.teams.getTaskCompletionByDepartment, requestData],
    postFetcher,
    swrOptions
  );

  const memoizedValue = useMemo(() => {
    const taskData = data?.data ?? {};

    return {
      departmentTasks: { taskData },
      departmentTasksLoading: !data && !error,
      departmentTasksError: error,
      departmentTasksValidating: isValidating,

      mutateDepartmentTasks: () =>
        mutate([apiEndpoints.teams.getTaskCompletionByDepartment, requestData]),
    };
  }, [data, error, isValidating, projectId, milestoneId]);

  return memoizedValue;
}
